/**
 * @module services/Auth.js
 */
import isBoolean from 'lodash/isBoolean';

import * as D from '../deps';

/**
 * Handle login request
 *
 * @param  {string} username - the username to supply to the api
 * @param  {string} password - the password to supply to the api
 * @param  {boolean} keepLoggedIn - whether or not users login state should be persisted
 * @param  {boolean} isUserInitiated - whether orn ot this request was initiated by user or done automatically
 * @param  {object} authToken - authToken in header. Overrides the default. We override it because LOGIN_ATTEMPT clears authToken and header defaults are empty there.
 * @param  {boolean} isReauth - whether or not this request is to be considered a reauth
 * @param  {boolean} [captchaState] - captcha field value
 * @returns {Promise}
 */
export const login = (
  username,
  password,
  keepLoggedIn = false,
  isUserInitiated = false,
  authToken = null,
  isReauth = false,
  captchaState,
) => {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.LOGIN,
    excludeAuthTokenInHeader: true,
    content: {
      existingAuthToken: authToken,
      username,
      password,
      keepLoggedIn,
      ...(isBoolean(captchaState) && { captcha: captchaState ? 1 : 0 }),
    },
    actionParams: {
      isUserInitiated,
      isReauth,
    },
    retries: 1,
    retryCondition: res => res.status === 500,
  });
};

export const loginWithAccessToken = (
  accessToken,
  keepLoggedIn,
  isUserInitiated = false,
  isReauth = false,
) => {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.LOGIN,
    excludeAuthTokenInHeader: true,
    content: {
      accessToken,
      keepLoggedIn,
    },
    actionParams: {
      isUserInitiated,
      isReauth,
    },
  });
};

export const register = ({
  username,
  password,
  zipcode,
  gender,
  birthYear,
  birthMonth,
  birthDay,
  keepLoggedIn,
}) => {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.REGISTER,
    content: {
      username,
      password,
      zipcode,
      gender,
      birthYear,
      birthMonth,
      birthDay,
      keepLoggedIn,
    },
  });
};

/**
 * Handle anonymous login request
 *
 * @param  {object} authToken - the token to auth request with
 * @param  {boolean} isLoginAttempt - whether or not this is operation is considered a login attempt
 * @param  {boolean} isCustomerServiceLogin - whether or not this is a customer service login.
 * @returns {Promise}
 */
export const anonLogin = ({ authToken, isLoginAttempt = true, isCustomerServiceLogin = false }) => {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.ANON_LOGIN,
    authToken,
    actionParams: {
      isLoginAttempt,
      isCustomerServiceLogin,
    },
    retries: 1,
    retryCondition: res => res.status === 500,
  });
};

export const deviceLogin = deviceUuid => {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.DEVICE_LOGIN,
    content: {
      deviceUuid,
    },
  });
};

/**
 * Handle logout request
 *
 * @param {string} username - the username to preserve on login page
 * @returns {Promise}
 */
export const logout = username => {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.LOGOUT,
    actionParams: {
      username,
    },
  });
};

/**
 * Sets community single sign-on cookie on login
 *
 * @param {string} authToken - the listener auth token
 * @returns {Promise}
 */
export const loginToCommunity = authToken => {
  return D.Api.apiRequest({
    method: 'post',
    endpoint: D.Constants.Api.ApiEndpoints.COMMUNITY_SSO,
    serviceHostType: D.Constants.Api.ServiceHostTypes.COMMUNITY_SSO,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    params: {
      auth_token: authToken,
    },
  });
};

/**
 * Removes community single sign-on cookie on logout
 *
 * @returns {Promise}
 */
export const logoutFromCommunity = () => {
  return D.Api.apiRequest({
    method: 'delete',
    endpoint: D.Constants.Api.ApiEndpoints.COMMUNITY_SSO,
    serviceHostType: D.Constants.Api.ServiceHostTypes.COMMUNITY_SSO,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
};

/**
 * Handle reset password request
 *
 * @param {string} resetToken - user token
 * @param {string} newPassword - password to set
 * @param  {boolean} [captchaState] - captcha field value
 * @returns {Promise}
 */
export const resetPassword = ({ resetToken, newPassword, captchaState }) => {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.RESET_PASSWORD,
    content: {
      resetToken,
      newPassword,
      ...(isBoolean(captchaState) && { captcha: captchaState ? 1 : 0 }),
    },
  });
};
